import React, { createContext, useContext, useRef, useEffect } from 'react'
import raf from 'raf'

const STICKY_EVENTS = [
  'resize',
  'scroll',
  'touchstart',
  'touchmove',
  'touchend',
  'pageshow',
  'load'
]

const StickyContext = createContext()

const StickyContainer = props => {
  const context = useRef({
    subscribers: [],
    framePending: undefined,
    rafHandle: null
  }).current
  const divRef = useRef()

  const subscribe = handler => {
    context.subscribers = context.subscribers.concat(handler)
  }

  const unsubscribe = handler => {
    context.subscribers = context.subscribers.filter(
      current => current !== handler
    )
  }

  const notifySubscribers = e => {
    if (!context.framePending) {
      const { currentTarget } = e

      context.rafHandle = raf(() => {
        context.framePending = false
        const { top, bottom } = divRef.current.getBoundingClientRect()

        context.subscribers.forEach(handler =>
          handler({
            distanceFromTop: top,
            distanceFromBottom: bottom,
            eventSource:
              currentTarget === window ? document.body : divRef.current
          })
        )
      })
      context.framePending = true
    }
  }

  useEffect(() => {
    STICKY_EVENTS.forEach(e => window.addEventListener(e, notifySubscribers))
    return () => {
      if (context.rafHandle) {
        raf.cancel(context.rafHandle)
        context.rafHandle = null
      }
      STICKY_EVENTS.forEach(e =>
        window.removeEventListener(e, notifySubscribers)
      )
    }
  }, [])

  return (
    <StickyContext.Provider
      value={{ subscribe, unsubscribe, getParent: () => divRef.current }}
    >
      <div
        {...props}
        ref={divRef}
        onScroll={notifySubscribers}
        onTouchStart={notifySubscribers}
        onTouchMove={notifySubscribers}
        onTouchEnd={notifySubscribers}
      />
    </StickyContext.Provider>
  )
}

export const useStickyContext = () => useContext(StickyContext)
export default StickyContainer
